/**
 * flattenBom.ts
 *
 * Cartesian flattening logic for BOM Aggregate view.
 * Combines config (code, colors, woods, assemblies) with line rows into flat rows.
 */
import type { BomAggregateConfigItem } from '@/services/boms/productBomSpreadsheetApiClient'
import type { FlatBomWoodRow, FlatBomHardwareRow } from './types'

export function flattenBomConfigs(configs: BomAggregateConfigItem[]): {
  woodRows: FlatBomWoodRow[]
  hardwareRows: FlatBomHardwareRow[]
} {
  const woodRows: FlatBomWoodRow[] = []
  const hardwareRows: FlatBomHardwareRow[] = []

  for (const cfg of configs) {
    const colors = cfg.colors && cfg.colors.length > 0 ? cfg.colors : ['—']
    const woods = cfg.woods && cfg.woods.length > 0 ? cfg.woods : ['—']
    const assemblies = cfg.assemblies && cfg.assemblies.length > 0 ? cfg.assemblies : ['—']

    for (const color of colors) {
      for (const woodElem of woods) {
        for (const assembly of assemblies) {
          const comboPrefix = `${cfg.id}-${color}-${woodElem}-${assembly}`

          // Flatten wood rows
          if (Array.isArray(cfg.wood)) {
            for (let i = 0; i < cfg.wood.length; i++) {
              const w = cfg.wood[i]
              woodRows.push({
                id: `${comboPrefix}-w-${w.id || i}`,
                code: cfg.code || '',
                color,
                wood: woodElem,
                assembly,
                part_name: w.part_name || '',
                wood_type_raw: w.wood_type_raw || '',
                dims_finished_t: w.dims_finished_t ?? '',
                dims_finished_w: w.dims_finished_w ?? '',
                dims_finished_l: w.dims_finished_l ?? '',
                tenon: w.tenon || '',
                qty_per: w.qty_per ?? '',
                veneer_spec: w.veneer_spec || '',
                notes: w.notes || '',
              })
            }
          }

          // Flatten hardware rows
          if (Array.isArray(cfg.hardware)) {
            for (let i = 0; i < cfg.hardware.length; i++) {
              const h = cfg.hardware[i]
              hardwareRows.push({
                id: `${comboPrefix}-h-${h.id || i}`,
                code: cfg.code || '',
                color,
                wood: woodElem,
                assembly,
                name_raw: h.name_raw || '',
                spec_raw: h.spec_raw || '',
                qty_per: h.qty_per ?? '',
                uom_hint: h.uom_hint || '',
                color_hint: h.color_hint || '',
                item_type: h.item_type || '',
                notes: h.notes || '',
              })
            }
          }
        }
      }
    }
  }

  return { woodRows, hardwareRows }
}
